import { useCallback } from 'react';
import { css } from '@linaria/core';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { NavigationIcon } from '@cbhq/cds-web/icons/NavigationIcon';
import { Box } from '@cbhq/cds-web/layout/Box';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { TextHeadline } from '@cbhq/cds-web/typography/TextHeadline';
import {
  ActionType,
  AnalyticsEventImportance,
  ComponentType,
  logEvent,
} from '@cbhq/client-analytics';

import config from ':cloud/config';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName } from ':cloud/init/clientAnalytics/logging';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import { useBuildQuestContext } from ':cloud/widgets/buildQuest/BuildQuestProvider';
import { useZoomable } from ':cloud/widgets/buildQuest/SimpleZoomable';

const outlinedButtonStyle = css`
  border-width: 2px;
  border-color: var(--foreground);
  border-radius: var(--border-radius-rounded-full);
`;

// create a shade effect (linear gradient) that doesn't have a harsh edge (blur)
const backgroundGradient = css`
  background: linear-gradient(0deg, transparent, var(--background) 90%);
  filter: blur(10px);
  top: -10px;
`;

export function BuildQuestNavigation() {
  const { isPhone } = useSimpleBreakpoints();
  const { togglePrizesModalOn } = useBuildQuestContext();

  const { resetTransform } = useZoomable();

  const handleLogoPress = useCallback((event: React.MouseEvent) => {
    logEvent(
      EventName.quest_logo_click,
      {
        action: ActionType.click,
        componentType: ComponentType.button,
      },
      AnalyticsEventImportance.high,
    );
    openUrlInNewWindow('https://portal.cdp.coinbase.com/');
    // Lets us retain the href and appropriate semantic <a> tag without losing our ability to open it in a new window
    event.preventDefault();
  }, []);

  const handleDiscordPress = useCallback(() => {
    logEvent(
      EventName.quest_discord_click,
      {
        action: ActionType.click,
        componentType: ComponentType.button,
      },
      AnalyticsEventImportance.high,
    );
    openUrlInNewWindow('https://discord.com/invite/cdp/');
  }, []);

  const handleCenterBoardPress = useCallback(() => resetTransform(), [resetTransform]);

  const handleViewPrizesPress = useCallback(() => {
    logEvent(
      EventName.quest_view_prizes_click,
      {
        action: ActionType.click,
        componentType: ComponentType.button,
      },
      AnalyticsEventImportance.high,
    );
    togglePrizesModalOn();
  }, [togglePrizesModalOn]);

  return (
    <Box
      spacing={isPhone ? 2 : 5}
      position="fixed"
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      zIndex={100}
    >
      <Box className={backgroundGradient} position="absolute" left={0} right={0} bottom={0} />
      <Pressable
        as="a"
        href="https://portal.cdp.coinbase.com/"
        background="transparent"
        noScaleOnPress
        accessibilityLabel="Coinbase Developer Platform"
        onPress={handleLogoPress}
      >
        <NavigationIcon name="coinbase" size="l" />
      </Pressable>

      {/* Right side buttons */}
      <HStack alignItems="center">
        <Box spacingEnd={1.5}>
          <Pressable
            as="button"
            background="transparent"
            className={outlinedButtonStyle}
            onPress={handleViewPrizesPress}
          >
            <Box spacingHorizontal={1} spacingVertical={0.5}>
              <TextHeadline as="p" color="foreground">
                {!isPhone ? 'View prizes' : 'Prizes'}
              </TextHeadline>
            </Box>
          </Pressable>
        </Box>
        <Button transparent compact onPress={handleDiscordPress} variant="secondary">
          <HStack alignItems="center" gap={1}>
            <Icon name="discordLogo" size="m" color="foreground" />
            {!isPhone ? 'Chat on Discord' : ''}
          </HStack>
        </Button>

        <Button transparent compact onPress={handleCenterBoardPress} variant="secondary">
          <HStack alignItems="center" gap={1}>
            <Icon name="qrCodeAlt" size="s" color="foreground" />
            {!isPhone ? 'Center board' : ''}
          </HStack>
        </Button>

        <Button transparent compact variant="secondary" href={config.logoutURL}>
          <HStack alignItems="center" gap={1}>
            <Icon name="logout" size="s" color="foreground" />
            {!isPhone ? 'Sign out' : ''}
          </HStack>
        </Button>
      </HStack>
    </Box>
  );
}
